import { createSelector } from "reselect";
import { ALL_FILTER, COMBO_FILTER, SPICY } from "../constants/menuItem";
import { MenuItem, Product, Variant } from "../types/product";
import { getSelectedFilter } from "./config";
import { RootState } from "../reducers";

type State = RootState;

interface Key {
  [key: string]: boolean;
}

interface ItemVariation {
  [key: string]: Variant[];
}

let keys: Key = {};

const productsSelector = (state: State): MenuItem[] =>
  state?.menuItems?.products || [];

const getMenuProductsLoading = (state: State): boolean =>
  Boolean(state?.menuItems?.productsLoading);

const byFilterOrder = (a: MenuItem, b: MenuItem): number =>
  a.product.filterOrder - b.product.filterOrder;

const matchesCategory = (menuItem: MenuItem, category: string): boolean =>
  category === COMBO_FILTER
    ? menuItem.isCombo
    : menuItem.product.filterCategories.includes(category);

const byCategory = (category: string) => (menuItem: MenuItem) => {
  const { id } = menuItem.product;
  if (matchesCategory(menuItem, category) && !keys[id]) {
    keys[id] = true;
    return true;
  }
  return false;
};

const selectFilteredProducts = (products: MenuItem[], category: string) => {
  if (category === ALL_FILTER) {
    return products;
  }
  keys = {};
  return products.filter(byCategory(category)).sort(byFilterOrder);
};

const getMenuItemsByCurrentFilterCategory = createSelector(
  productsSelector,
  getSelectedFilter,
  selectFilteredProducts,
);

const getItemVariations = createSelector(productsSelector, (products) => {
  return products.reduce((allVariations: ItemVariation, product) => {
    const variations = product?.product?.variations;
    if (variations.length > 0) {
      variations.forEach((variation) => {
        let existingVariations = allVariations[variation.productId];
        if (!existingVariations || existingVariations.length === 0) {
          existingVariations = [];
        }
        existingVariations.push(variation);
        allVariations = {
          ...allVariations,
          [variation.productId]: existingVariations,
        };
      });
    }
    return allVariations;
  }, {});
});

const getItemsByVariation = (variationName: string) =>
  createSelector(
    [
      (state: State) => getItemVariations(state),
      (_state: State, props: { product: Product }) => props.product.id,
    ],
    (variations, productId): Variant | undefined => {
      const itemVariations = variations[productId];
      if (itemVariations && itemVariations.length > 0) {
        return itemVariations.find((v) => v.name === variationName);
      }
      return undefined;
    },
  );

const getIfItemSpicy = getItemsByVariation(SPICY);

export {
  productsSelector,
  getMenuProductsLoading,
  getMenuItemsByCurrentFilterCategory,
  getItemVariations,
  getItemsByVariation,
  getIfItemSpicy,
};
